import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash, faCheckCircle, faCircle, faList } from '@fortawesome/free-solid-svg-icons';
import { X, Save, Info } from 'lucide-react';
import EventLogSection from '../../../../components/EventLogSection/EventLogSection';
import useEventLogModalScrollLock from '../../../../hooks/useEventLogModalScrollLock';

import { OverlayTrigger, Tooltip, Modal, Button } from 'react-bootstrap';
import { createAdmin, updateAdmin, fetchAdmin, fetchAdmins, clearCurrentAdmin } from '../../../../store/slices/adminSlice';
import { fetchPractice } from '../../../../store/slices/practiceSlice';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import adminService from '../../../../services/adminService';
import practiceService from '../../../../services/practiceService';
import { toast } from 'react-toastify';
import { matchesWordStart } from '../../../../utils/searchMatch';
import './AddAdmin.css';
import '../../PatientManagement/AddPatient/AddPatientModal.css';
import '../../../SystemCaregiverDashboard/AddSystemCaregiver/AddSystemCaregiver.css';
import { validatePassword, applyPasswordServerFieldError, formatDisplayError } from '../../../../utils/passwordValidation';
import { TIME_ZONES, TITLES, US_STATES, normalizeTimeZone } from '../../../../utils/constants';
import AddFormModel from '../../../../components/Common/AddFormModel/AddFormModel';
import FormActionButtons from '../../../../components/Common/FormActionButtons/FormActionButtons';
import FormDetailSkeleton from '../../../../components/SkeletonLoader/FormDetailSkeleton';
import useFocusTrap from '../../../../hooks/useFocusTrap';
import { createModalEscapeKeyDownHandler } from '../../../../utils/modalEscape';
import { VALIDATION_SUMMARY_MESSAGE, preserveAvailabilityErrors, clearFieldError, validateRequiredPhone, updateFieldErrorOnChange } from '../../../../utils/formValidation';
import { formatUSPhoneNumber, extractPhoneDigits, getPhoneTypingError } from '../../../../utils/phoneUtils';
import PhoneNumberInput from '../../../../components/Common/PhoneNumberInput/PhoneNumberInput';
import { sanitizeEmailInput, getEmailValidationError } from '../../../../utils/emailValidation';
import { sanitizeUsernameInput, getUsernameFormatError, USERNAME_MAX_LENGTH } from '../../../../utils/usernameValidation';
import useUsernameEmailAvailability from '../../../../hooks/useUsernameEmailAvailability';
import { normalizeAdminStatus } from '../../../../utils/adminStatus';

const AddAdminModal = ({ show, onHide, practiceId: propPracticeId, isEditMode: propIsEditMode, editId: propEditId, isSystemAdmin: propIsSystemAdmin, isViewMode: propIsViewMode = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const [searchParams] = useSearchParams();

  const { isCreating, isUpdating, error, currentAdmin } = useSelector(state => state.admin);
  const { currentPractice } = useSelector(state => state.practice ?? {});
  const { updateBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemAdmin, setSystemAdmin] = useState(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showPasswordCriteria, setShowPasswordCriteria] = useState(false);
  const [errors, setErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [practices, setPractices] = useState([]);
  const [selectedPractices, setSelectedPractices] = useState([]);
  const [loadingPractices, setLoadingPractices] = useState(false);
  const [showPracticeDropdown, setShowPracticeDropdown] = useState(false);
  const [practiceSearchTerm, setPracticeSearchTerm] = useState('');
  const practiceDropdownRef = useRef(null);
  const [initialSelectedPractices, setInitialSelectedPractices] = useState([]);

  // Check if we're in edit mode
  const editId = propEditId || searchParams.get("editId");
  const isViewMode = Boolean(propIsViewMode);
  const isEditMode = !isViewMode && (propIsEditMode || Boolean(editId));
  const isRecordMode = isViewMode || isEditMode;
  const [showEventLogsModal, setShowEventLogsModal] = useState(false);
  const logOverlayRef = useRef(null);
  const logContainerRef = useRef(null);
  useEventLogModalScrollLock(showEventLogsModal, logOverlayRef, logContainerRef);
  const isSystemAdmin = propIsSystemAdmin || !practiceId; // System admin if no practiceId in route
  const excludeUserId = isRecordMode ? (editId ?? systemAdmin?.id ?? currentAdmin?.id ?? null) : null;
  const [loadedCredentials, setLoadedCredentials] = useState({ username: '', email: '' });
  const {
    scheduleUsernameCheck,
    scheduleEmailCheck,
    handleUsernamePaste,
    handleUsernameBlur,
    handleEmailPaste,
    ensureUsernameAvailable,
    ensureEmailAvailable,
  } = useUsernameEmailAvailability({
    excludeUserId,
    originalUsername: loadedCredentials.username,
    originalEmail: loadedCredentials.email,
  });

  // Fetch admin data when in edit mode
  useEffect(() => {
    if (isRecordMode && editId) {
      if (isSystemAdmin) {
        // Fetch system admin
        setSystemAdmin(null);
        setLoadingAdmin(true);
        adminService.getSystemAdmin(editId)
          .then(response => {
            if (response.data && response.data.success) {
              setSystemAdmin(response.data.data);
            }
          })
          .catch(error => {
            console.error('Error fetching system admin:', error);
          })
          .finally(() => {
            setLoadingAdmin(false);
          });
      } else {
        // Fetch practice admin
        dispatch(clearCurrentAdmin());
        dispatch(fetchAdmin({ practiceId, adminId: editId }));
      }
    }
  }, [isRecordMode, editId, practiceId, isSystemAdmin, dispatch]);

  // Fetch practice data if not already state
  useEffect(() => {
    if (practiceId && (!currentPractice || String(currentPractice.id) !== String(practiceId))) {
      dispatch(fetchPractice(practiceId));
    }
  }, [practiceId, currentPractice, dispatch]);

  useEffect(() => {
    return () => {
      clearBreadcrumbs();
    };
  }, [clearBreadcrumbs]);

  useEffect(() => {
    if (isSystemAdmin) {
      updateBreadcrumbs([
        { label: 'System Admins', path: '/admin' },
        { label: isViewMode ? 'View System Admin' : isEditMode ? 'Edit System Admin' : 'Add System Admin', path: null, active: true }
      ]);
    } else {
      const practiceName = currentPractice?.practice_name || 'Practice';
      const fromDashboard = !!location.state?.fromDashboard;
      const rootLabel = fromDashboard ? 'Dashboard' : 'Practices';
      const rootPath = fromDashboard ? '/dashboard' : '/practices';
      const dashboardTabSuffix = fromDashboard ? '&fromDashboard=true' : '';

      updateBreadcrumbs([
        { label: rootLabel, path: rootPath },
        { label: practiceName, path: `${fromDashboard ? '/dashboard' : '/practices'}/${practiceId}?tab=practice${dashboardTabSuffix}` },
        { label: isViewMode ? 'View Practice Admin' : isEditMode ? 'Edit Practice Admin' : 'Add Practice Admin', path: null, active: true }
      ]);
    }

  }, [isSystemAdmin, isEditMode, isViewMode, practiceId, currentPractice, updateBreadcrumbs]);

  useEffect(() => {
    if (!isSystemAdmin || !show) return;

    const handleClickOutside = (event) => {
      if (practiceDropdownRef.current && !practiceDropdownRef.current.contains(event.target)) {
        setShowPracticeDropdown(false);
      }
    };

    if (showPracticeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSystemAdmin, show, showPracticeDropdown]);

  useEffect(() => {
    if (!showPracticeDropdown) {
      setPracticeSearchTerm('');
    }
  }, [showPracticeDropdown]);

  useEffect(() => {
    if (!isSystemAdmin || !show) return;

    const fetchPractices = async () => {
      try {
        setLoadingPractices(true);
        const response = await practiceService.getPractices({ limit: 1000 });

        let practicesList = [];
        if (response?.data?.success && response.data.data) {
          if (Array.isArray(response.data.data.practices)) {
            practicesList = response.data.data.practices;
          } else if (Array.isArray(response.data.data)) {
            practicesList = response.data.data;
          }
        } else if (Array.isArray(response?.data)) {
          practicesList = response.data;
        }

        setPractices(practicesList);
      } catch (error) {
        console.error('Error fetching practices:', error);
        toast.error('Failed to load practices');
      } finally {
        setLoadingPractices(false);
      }
    };

    fetchPractices();
  }, [isSystemAdmin, show]);

  useEffect(() => {
    if (show && !isEditMode && isSystemAdmin) {
      setSelectedPractices([]);
      setInitialSelectedPractices([]);
    }
  }, [show, isEditMode, isSystemAdmin]);

  const handlePracticeClick = (practice) => {
    setSelectedPractices((prev) => {
      if (prev.find((p) => p.practiceId === practice.id)) {
        return prev;
      }
      return [
        ...prev,
        {
          practiceId: practice.id,
          practiceName: practice.practice_name || `Practice ${practice.id}`
        }
      ];
    });
    setShowPracticeDropdown(false);
    setPracticeSearchTerm('');
    setErrors((prev) => {
      const { practice_ids, ...rest } = prev;
      return rest;
    });
  };

  const handleRemovePractice = (practiceId) => {
    setSelectedPractices((prev) => prev.filter((p) => p.practiceId !== practiceId));
  };

  const [formData, setFormData] = useState({
    title: '',
    firstName: '',
    lastName: '',
    middleInitial: '',
    email: '',
    phone: '',
    cellPhone: '',
    fax: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '', // Full Name
    stateId: null, // backend ID
    zipCode: '',
    username: '',
    password: '',
    confirmPassword: '',
    status: 'Active',
    twoWayAuth: 'Disabled',
    timeZone: 'America/New_York'

  });

  const [initialFormData, setInitialFormData] = useState(null);

  const isChanged = React.useMemo(() => {
    if (!isEditMode || !initialFormData) return true;
    if (formData.password?.trim() || formData.confirmPassword?.trim()) return true;
    const formChanged = JSON.stringify({ ...formData, password: '', confirmPassword: '' }) !==
      JSON.stringify({ ...initialFormData, password: '', confirmPassword: '' });
    if (isSystemAdmin) {
      const practicesChanged = JSON.stringify(selectedPractices.map((p) => p.practiceId).sort()) !==
        JSON.stringify(initialSelectedPractices.map((p) => p.practiceId).sort());
      return formChanged || practicesChanged;
    }
    return formChanged;
  }, [formData, initialFormData, isEditMode, isSystemAdmin, selectedPractices, initialSelectedPractices]);

  // Populate form data when admin is loaded
  useEffect(() => {
    if (isRecordMode) {
      const adminData = isSystemAdmin ? systemAdmin : currentAdmin;
      if (adminData && String(adminData.id) === String(editId)) {
        const mappedData = {
          title: (adminData.title_id !== null && adminData.title_id !== undefined)
            ? String(adminData.title_id)
            : (TITLES.find(t => t.label === adminData.title)?.id ? String(TITLES.find(t => t.label === adminData.title).id) : ''),
          firstName: adminData.first_name || '',
          lastName: adminData.last_name || '',
          middleInitial: adminData.middle_name || adminData.middle_initial || '',
          email: adminData.email || '',
          addressLine1: adminData.address_line_1 || '',
          addressLine2: adminData.address_line_2 || '',
          city: adminData.city || '',
          state: US_STATES.find(s => s.id === adminData.state_id)?.name || '',
          stateId: adminData.state_id || null,
          zipCode: adminData.zip_code ? String(adminData.zip_code) : '',
          phone: formatPhoneNumber(adminData.cell_phone || ''),
          cellPhone: formatPhoneNumber(adminData.phone || adminData.home_phone || ''),
          fax: formatPhoneNumber(adminData.fax || ''),
          username: adminData.username || adminData.user?.username || '',
          status: normalizeAdminStatus(adminData.status),
          twoWayAuth: (adminData.is_two_way_auth === 1 || adminData.is_two_way_auth === true || adminData.two_way_auth === 'Enabled' || adminData.two_way_authentication === 'Enabled') ? 'Enabled' : 'Disabled',
          timeZone: normalizeTimeZone(adminData.time_zone || adminData.timeZone || adminData.timezone) || '',
          password: '',
          confirmPassword: ''
        };
        setFormData(mappedData);
        setInitialFormData(mappedData);
        setLoadedCredentials({
          username: mappedData.username || '',
          email: mappedData.email || '',
        });

        if (isSystemAdmin && adminData.practices) {
          const mappedPractices = adminData.practices.map((practice) => ({
            practiceId: practice.id,
            practiceName: practice.name || practice.practice_name || `Practice ${practice.id}`
          }));
          setSelectedPractices(mappedPractices);
          setInitialSelectedPractices(mappedPractices);
        } else if (isSystemAdmin && Array.isArray(adminData.practice_ids) && adminData.practice_ids.length > 0) {
          const mappedPractices = adminData.practice_ids.map((practiceId) => ({
            practiceId,
            practiceName: `Practice ${practiceId}`
          }));
          setSelectedPractices(mappedPractices);
          setInitialSelectedPractices(mappedPractices);
        }
      }
    }
  }, [isRecordMode, currentAdmin, systemAdmin, isSystemAdmin, editId]);
  // Helper for phone formatting (XXX) XXX - XXXX
  const formatPhoneNumber = formatUSPhoneNumber;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    clearFieldError(setErrors, name);
    let newErrors = { ...errors };

    // Clear existing error for current field before re-validating
    delete newErrors[name];

    // 1. Phone Number Masking & Validation
    if (['phone', 'cellPhone', 'fax'].includes(name)) {
      const formatted = value;
      const digits = extractPhoneDigits(formatted);
      setFormData((prev) => ({ ...prev, [name]: formatted }));

      const typingError = getPhoneTypingError(digits, {
        required: name === 'cellPhone',
        requiredLabel: name === 'phone' ? 'Home Phone' : name === 'cellPhone' ? 'Cell Phone' : 'Fax',
      });

      setErrors((prev) => {
        const next = { ...prev };
        if (typingError) next[name] = typingError;
        else delete next[name];
        return next;
      });
      return;
    }

    // 1.1 Zip Code Validation (Prevention)
    if (name === 'zipCode') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 5) {
        setFormData(prev => ({ ...prev, [name]: numericValue }));
        if (numericValue.length > 0 && numericValue.length < 5) {
          newErrors.zipCode = 'Zip code must be exactly 5 digits';
        } else {
          delete newErrors.zipCode;
        }
        setErrors(newErrors);
      }
      return;
    }

    // 2. Strict Name Validation
    if (['firstName', 'lastName', 'middleInitial'].includes(name)) {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
      if (value.length > 20) {
        return;
      }
    }

    // 2.1 State Sync
    if (name === 'state') {
      const selectedState = US_STATES.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        state: value,
        stateId: selectedState ? selectedState.id : null
      }));
      updateFieldErrorOnChange(setErrors, 'state', value);
      return;
    }

    // 3. Password Validation (Real-time)
    if (name === 'password') {
      if (!value) {
        setErrors(prev => {
          const { password, ...rest } = prev;
          return rest;
        });
      } else {
        const userData = {
          firstName: formData.firstName,
          lastName: formData.lastName
        };
        const validation = validatePassword(value, userData);
        if (!validation.isValid) {
          setErrors(prev => ({ ...prev, password: validation.error }));
        } else {
          setErrors(prev => {
            const { password, ...rest } = prev;
            return rest;
          });
        }
      }
    }

    // City Validation (Alpha only)
    if (name === 'city') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // 4. Email Validation (Real-time + Character Filtering)
    if (name === 'email') {
      const cleanedValue = sanitizeEmailInput(value);
      setFormData(prev => ({ ...prev, email: cleanedValue }));

      const emailErr = getEmailValidationError(cleanedValue);
      if (emailErr) {
        setErrors(prev => ({ ...prev, email: emailErr }));
      } else {
        setErrors(prev => {
          const { email, ...rest } = prev;
          return rest;
        });
        scheduleEmailCheck(cleanedValue, setErrors);
      }
      return;
    }

    // Username validation (letters, numbers, ., _)
    if (name === 'username') {
      const cleanedValue = sanitizeUsernameInput(value);
      setFormData(prev => ({ ...prev, username: cleanedValue }));
      const formatError = getUsernameFormatError(cleanedValue);
      if (!cleanedValue) {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
      } else if (formatError) {
        setErrors(prev => ({ ...prev, username: formatError }));
      } else {
        setErrors(prev => {
          const { username, ...rest } = prev;
          return rest;
        });
        scheduleUsernameCheck(cleanedValue, setErrors);
      }
      return;
    }

    if (name === 'confirmPassword') {
      if (!value && !isEditMode) {
        newErrors.confirmPassword = 'Confirm Password is required';
      } else if (formData.password && value !== formData.password) {
        newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
      } else {
        delete newErrors.confirmPassword;
      }
    }

    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      username: 'Username',
      addressLine1: 'Address Line 1',
      city: 'City',
      state: 'State',
      zipCode: 'Zip Code',
      timeZone: 'Time Zone',
      twoWayAuth: 'Two Way Authentication'

    };

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name !== 'password' && name !== 'email') {
      updateFieldErrorOnChange(setErrors, name, value, {
        required: Boolean(fieldLabels[name]),
        requiredMessage: fieldLabels[name] ? `${fieldLabels[name]} is required` : null,
      });
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName?.trim()) newErrors.firstName = 'First Name is required';
    if (!formData.lastName?.trim()) newErrors.lastName = 'Last Name is required';
    if (!formData.username?.trim()) {
      newErrors.username = 'Username is required';
    } else {
      const formatError = getUsernameFormatError(formData.username);
      if (formatError) newErrors.username = formatError;
    }
    if (!formData.timeZone) newErrors.timeZone = 'Time Zone is required';
    if (!formData.twoWayAuth) newErrors.twoWayAuth = 'Two Way Authentication is required';


    // Password validation
    if (!isEditMode) {
      if (!formData.password?.trim()) {
        newErrors.password = 'Password is required';
      } else {
        const userData = { firstName: formData.firstName, lastName: formData.lastName };
        const validation = validatePassword(formData.password, userData);
        if (!validation.isValid) newErrors.password = validation.error;
      }
    } else if (formData.password?.trim()) {
      const userData = { firstName: formData.firstName, lastName: formData.lastName };
      const validation = validatePassword(formData.password, userData);
      if (!validation.isValid) newErrors.password = validation.error;
    }

    // Confirm Password validation
    if (formData.password && formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match. Please re-enter.';
    }

    if (!formData.addressLine1?.trim()) newErrors.addressLine1 = 'Address Line 1 is required';
    if (!formData.city?.trim()) newErrors.city = 'City is required';
    if (!formData.state || formData.state === 'Select') newErrors.state = 'State is required';

    if (!formData.zipCode?.trim()) {
      newErrors.zipCode = 'Zip Code is required';
    } else if (formData.zipCode.trim().length !== 5) {
      newErrors.zipCode = 'Zip code must be exactly 5 digits';
    }

    const emailErr = getEmailValidationError((formData.email || '').trim());
    if (emailErr) newErrors.email = emailErr;

    const homePhoneError = validateRequiredPhone(formData.phone, { required: false, label: 'Home Phone' });
    if (homePhoneError) newErrors.phone = homePhoneError;

    const cellPhoneError = validateRequiredPhone(formData.cellPhone, { label: 'Cell Phone' });
    if (cellPhoneError) newErrors.cellPhone = cellPhoneError;

    const faxError = validateRequiredPhone(formData.fax, { required: false, label: 'Fax' });
    if (faxError) newErrors.fax = faxError;

    if (isSystemAdmin && selectedPractices.length === 0) {
      newErrors.practice_ids = 'At least one practice must be assigned';
    }

    const mergedErrors = preserveAvailabilityErrors(errors, newErrors);
    setErrors(mergedErrors);
    return Object.keys(mergedErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!validateForm()) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    const usernameOk = await ensureUsernameAvailable(formData.username, setErrors);
    const emailOk = await ensureEmailAvailable(formData.email, setErrors);
    if (!usernameOk || !emailOk) {
      setIsSubmitting(false);
      toast.warn(VALIDATION_SUMMARY_MESSAGE);
      return;
    }

    try {
      let result;
      // Filter out fields that the backend doesn't expect
      const filteredFormData = {
        first_name: formData.firstName || '',
        last_name: formData.lastName || '',
        middle_name: formData.middleInitial || '',
        email: formData.email || '',
        phone: formData.cellPhone || '',
        cell_phone: formData.phone || '',
        fax: formData.fax || '',
        address_line_1: formData.addressLine1 || '',
        address_line_2: formData.addressLine2 || '',
        city: formData.city || '',
        state_id: (formData.stateId !== undefined && formData.stateId !== null && formData.stateId !== '')
          ? parseInt(formData.stateId)
          : 1,
        zip_code: formData.zipCode ? String(formData.zipCode) : '',
        username: formData.username || '',
        status: formData.status || 'Active',
        two_way_auth: formData.twoWayAuth || 'Disabled',
        time_zone: formData.timeZone,
        title_id: parseInt(formData.title) || null
      };

      if (formData.password && formData.password.trim() !== '') {
        filteredFormData.password = formData.password.trim();
      }

      if (isSystemAdmin) {
        filteredFormData.practice_ids = selectedPractices.map((p) => p.practiceId);
      }

      console.log('🔧 Filtered form data before sending:', filteredFormData);

      if (isEditMode) {
        result = await dispatch(updateAdmin({
          practiceId: isSystemAdmin ? null : practiceId,
          adminId: editId,
          adminData: filteredFormData
        })).unwrap();
      } else {
        result = await dispatch(createAdmin({
          practiceId: isSystemAdmin ? null : practiceId,
          adminData: {
            ...filteredFormData,
            password: formData.password?.trim() || ''
          }
        })).unwrap();
      }

      if (isSystemAdmin) {
        toast.success(isEditMode ? 'System admin updated successfully!' : 'System admin added successfully!');
      } else {
        toast.success(isEditMode ? 'Practice admin updated successfully!' : 'Practice admin added successfully!');
      }
      if (onHide) onHide();

    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} admin:`, error);
      // Extract the most specific error message from the API response
      let apiMsg = formatDisplayError(error);
      const lower = apiMsg.toLowerCase();
      let hasUserErr = false;
      let hasEmailErr = false;

      if (lower.includes('username') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, username: 'This username is already taken. Please choose a different username.' }));
        hasUserErr = true;
      }
      if (lower.includes('email') && (lower.includes('exist') || lower.includes('taken') || lower.includes('duplicate'))) {
        setErrors(prev => ({ ...prev, email: 'This email address is already registered. Please use a different email.' }));
        hasEmailErr = true;
      }

      if (applyPasswordServerFieldError(setErrors, apiMsg)) {
        return;
      }

      let friendlyMsg = '';
      if (hasUserErr && hasEmailErr) {
        friendlyMsg = 'This username is already taken and this email address is already registered. Please use different values.';
      } else if (hasUserErr) {
        friendlyMsg = 'This username is already taken. Please choose a different username.';
      } else if (hasEmailErr) {
        friendlyMsg = 'This email address is already registered. Please use a different email.';
      } else {
        friendlyMsg = apiMsg || `Failed to ${isEditMode ? 'update' : 'add'} admin. Please try again.`;
      }
      if (friendlyMsg) {
        toast.error(friendlyMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isSystemAdmin) {
      navigate('/admin');
    } else {
      const fromDashboard = location.state?.fromDashboard;
      const path = fromDashboard ? `/dashboard/${practiceId}?tab=admin` : `/practices/${practiceId}?tab=admin`;
      navigate(path, { state: { fromDashboard } });
    }
  };


  // Get logo URL - construct full URL if logo path exists
  const getLogoUrl = () => {
    if (!currentPractice?.practice_logo) return null;
    const logo = currentPractice.practice_logo;
    if (logo.startsWith('http://') || logo.startsWith('https://')) return logo;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const baseUrl = API_URL.replace('/api', '');
    const logoPath = logo.startsWith('/') ? logo : `/${logo}`;
    return `${baseUrl}${logoPath}`;
  };

  const showEditLoading = isRecordMode && (isSystemAdmin ? loadingAdmin : !currentAdmin);

  // Focus trap: ref attached to modal content, hook active when modal is open
  const modalRef = useRef(null);
  const handleEscapeKeyDown = createModalEscapeKeyDownHandler(onHide);
  useFocusTrap(modalRef, !!show, onHide);

  if (!show) return null;

  if (showEditLoading) {
    return (
      <>
        <div className="pif-modal-overlay"></div>
        <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
          <div className="pif-container" style={{ padding: '30px' }}>
            <FormDetailSkeleton titleLines={1} fieldRows={8} columns={2} showFooter={false} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="pif-modal-overlay"></div>
      <div className="pif-modal-content" ref={modalRef} role="dialog" aria-modal="true" onKeyDown={handleEscapeKeyDown}>
        <div className="pif-container">
          <div className="pif-stepper-header">
            <button className="pif-modal-close-btn" type="button" onClick={onHide}><X size={20} /></button>
            <h2 className="pif-stepper-title">
              {isSystemAdmin
                ? (isViewMode ? 'View System Admin' : isEditMode ? 'Edit System Admin' : 'Add System Admin')
                : (isViewMode ? 'View Practice Admin' : isEditMode ? 'Edit Practice Admin' : 'Add Practice Admin')}
            </h2>
          </div>

          <div className="pif-content-wrapper">
            <form onSubmit={handleSubmit} noValidate>
              <fieldset disabled={isViewMode} className="pif-view-fieldset">
                <div className="pif-content">
                  <div className="pif-aligned-grid">

                    {/* Personal Info */}
                    <div className="pif-demo-section-label">Personal Information</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-title-group">
                          <label htmlFor="title">Title</label>
                          <select id="title" name="title" className={`pif-input ${errors.title ? "is-invalid" : ""}`} value={formData.title} onChange={handleInputChange}>
                            <option value="">-</option>
                            {TITLES && TITLES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                          </select>
                          {errors.title && <span className="text-red-500" style={{ fontSize: '11px', display: 'block', marginTop: '4px' }}>{errors.title}</span>}
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="firstName">First Name <span className="text-red-500">*</span></label>
                          <input type="text" id="firstName" name="firstName" className={`pif-input ${errors.firstName ? "is-invalid" : ""}`} value={formData.firstName} onChange={handleInputChange} placeholder="e.g., John" required />
                          {errors.firstName && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.firstName}</span>}
                        </div>
                      </div>
                      <div className="pif-form-row" style={{ flex: 1 }}>
                        <div className="pif-form-group pif-mi-group">
                          <label htmlFor="middleInitial">Middle Name</label>
                          <input type="text" id="middleInitial" name="middleInitial" className={`pif-input ${errors.middleInitial ? "is-invalid" : ""}`} value={formData.middleInitial} onChange={handleInputChange} placeholder="e.g., Marie" maxLength={20} />
                          {errors.middleInitial && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.middleInitial}</span>}
                        </div>
                        <div className="pif-form-group pif-flex-1">
                          <label htmlFor="lastName">Last Name <span className="text-red-500">*</span></label>
                          <input type="text" id="lastName" name="lastName" className={`pif-input ${errors.lastName ? "is-invalid" : ""}`} value={formData.lastName} onChange={handleInputChange} placeholder="e.g., Doe" required />
                          {errors.lastName && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.lastName}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="phone">Home Phone</label>
                        <PhoneNumberInput id="phone" name="phone" className={`pif-input ${errors.phone ? "is-invalid" : ""}`} value={formData.phone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.phone && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.phone}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="cellPhone">Cell Phone <span className="text-red-500">*</span></label>
                        <PhoneNumberInput id="cellPhone" name="cellPhone" className={`pif-input ${errors.cellPhone ? "is-invalid" : ""}`} value={formData.cellPhone} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.cellPhone && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.cellPhone}</span>}
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group" style={{ flex: 0.5 }}>
                        <label htmlFor="fax">Fax</label>
                        <PhoneNumberInput id="fax" name="fax" className={`pif-input ${errors.fax ? "is-invalid" : ""}`} value={formData.fax} onChange={handleInputChange} placeholder="(000) 000-0000" />
                        {errors.fax && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.fax}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Account Credentials</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="username">Username <span className="text-red-500">*</span></label>
                        <input type="text" id="username" name="username" className={`pif-input ${errors.username ? "is-invalid" : ""}`} value={formData.username} onChange={handleInputChange} onPaste={(e) => handleUsernamePaste(e, setErrors)} onBlur={() => handleUsernameBlur(formData.username, setErrors)} placeholder="e.g., john.doe" required maxLength={USERNAME_MAX_LENGTH} />
                        {errors.username && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.username}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="email">Email <span className="text-red-500">*</span></label>
                        <input type="email" id="email" name="email" className={`pif-input ${errors.email ? "is-invalid" : ""}`} value={formData.email} onChange={handleInputChange} onPaste={(e) => handleEmailPaste(e, setErrors)} placeholder="e.g., admin@example.com" required />
                        {errors.email && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.email}</span>}
                      </div>
                    </div>

                    {!isViewMode && (
                      <div className="pif-form-row-balanced">
                        <div className="pif-form-group">
                          <label htmlFor="password">Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showPassword ? "text" : "password"} id="password" name="password" className={`pif-input ${errors.password ? "is-invalid" : ""}`} value={formData.password} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" onFocus={() => setShowPasswordCriteria(true)} onBlur={() => setShowPasswordCriteria(false)} style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={togglePasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </button>
                            {showPasswordCriteria && (
                              <div className="ap-password-criteria-block">
                                <div className={`ap-criteria-item ${formData.password.length >= 8 ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={formData.password.length >= 8 ? faCheckCircle : faCircle} className="ap-criteria-icon" /> Minimum 8 characters
                                </div>
                                <div className={`ap-criteria-item ${/[A-Z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[A-Z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Uppercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[a-z]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[a-z]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Lowercase letter
                                </div>
                                <div className={`ap-criteria-item ${/[0-9]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[0-9]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Number
                                </div>
                                <div className={`ap-criteria-item ${/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'fulfilled' : ''}`}>
                                  <FontAwesomeIcon icon={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? faCheckCircle : faCircle} className="ap-criteria-icon" /> At least 1 Special character
                                </div>
                              </div>
                            )}
                          </div>
                          {errors.password && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.password}</span>}
                        </div>
                        <div className="pif-form-group">
                          <label htmlFor="confirmPassword">Confirm Password {!isEditMode && <span className="text-red-500">*</span>}</label>
                          <div className="ap-password-wrapper" style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                            <input type={showConfirmPassword ? "text" : "password"} id="confirmPassword" name="confirmPassword" className={`pif-input ${errors.confirmPassword ? "is-invalid" : ""}`} value={formData.confirmPassword} onChange={handleInputChange} placeholder={isEditMode ? "Leave blank to keep current" : "Enter at least 8 characters"} required={!isEditMode} autoComplete="new-password" style={{ paddingRight: '40px' }} />
                            <button type="button" onClick={toggleConfirmPasswordVisibility} style={{ position: 'absolute', right: '10px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                              <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                            </button>
                          </div>
                          {errors.confirmPassword && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.confirmPassword}</span>}
                        </div>
                      </div>
                    )}

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Address & Location</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine1" style={{ marginBottom: 0 }}>Address Line 1 <span className="text-red-500">*</span></label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine1?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine1" name="addressLine1" maxLength={250} className={`pif-input ${errors.addressLine1 ? "is-invalid" : ""}`} value={formData.addressLine1} onChange={handleInputChange} placeholder="e.g., 123 Main St" required />
                        {errors.addressLine1 && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.addressLine1}</span>}
                      </div>
                      <div className="pif-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '8px' }}>
                          <label htmlFor="addressLine2" style={{ marginBottom: 0 }}>Address Line 2</label>
                          <span style={{ fontSize: '11px', color: '#64748b' }}>{formData.addressLine2?.length || 0}/250</span>
                        </div>
                        <input type="text" id="addressLine2" name="addressLine2" maxLength={250} className={`pif-input ${errors.addressLine2 ? "is-invalid" : ""}`} value={formData.addressLine2} onChange={handleInputChange} placeholder="e.g., Apt 4B" />
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="city">City <span className="text-red-500">*</span></label>
                        <input type="text" id="city" name="city" className={`pif-input ${errors.city ? "is-invalid" : ""}`} value={formData.city} onChange={handleInputChange} placeholder="e.g., New York" required />
                        {errors.city && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.city}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="state">State <span className="text-red-500">*</span></label>
                        <select id="state" name="state" className={`pif-input ${errors.state ? "is-invalid" : ""}`} value={formData.state} onChange={handleInputChange} required>
                          <option value="">- Select State -</option>
                          {US_STATES && US_STATES.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
                        </select>
                        {errors.state && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.state}</span>}
                      </div>
                    </div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="zipCode">Zip Code <span className="text-red-500">*</span></label>
                        <input type="text" id="zipCode" name="zipCode" className={`pif-input ${errors.zipCode ? "is-invalid" : ""}`} value={formData.zipCode} onChange={handleInputChange} placeholder="e.g., 10001" maxLength="5" required />
                        {errors.zipCode && <span className="text-red-500" style={{ fontSize: '11px' }}>{errors.zipCode}</span>}
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="timeZone">Time Zone <span className="text-red-500">*</span></label>
                        <select id="timeZone" name="timeZone" className={`pif-input ${errors.timeZone ? "is-invalid" : ""}`} value={formData.timeZone} onChange={handleInputChange} required>
                          <option value="">Select Time Zone</option>
                          {TIME_ZONES && TIME_ZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                        {errors.timeZone && <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>{errors.timeZone}</span>}
                      </div>
                    </div>

                    <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Additional Settings</div>

                    <div className="pif-form-row-balanced">
                      <div className="pif-form-group">
                        <label htmlFor="status">Status <span className="text-red-500">*</span></label>
                        <select id="status" name="status" className={`pif-input ${errors.status ? "is-invalid" : ""}`} value={formData.status} onChange={handleInputChange}>
                          <option value="Active">Active</option>
                          <option value="Inactive">Inactive</option>
                        </select>
                      </div>
                      <div className="pif-form-group">
                        <label htmlFor="twoWayAuth">Two-Way Authentication</label>
                        <select id="twoWayAuth" name="twoWayAuth" className={`pif-input ${errors.twoWayAuth ? "is-invalid" : ""}`} value={formData.twoWayAuth} onChange={handleInputChange}>
                          <option value="Enabled">Enabled</option>
                          <option value="Disabled">Disabled</option>
                        </select>
                      </div>
                    </div>

                    {isSystemAdmin && (
                      <>
                        <div className="pif-demo-section-label" style={{ marginTop: '10px' }}>Practice Assignment</div>

                        <div className="pif-form-row-balanced">
                          <div className="pif-form-group" style={{ position: 'relative' }} ref={practiceDropdownRef}>
                            <label>Assigned Practices {!isViewMode && <span className="text-red-500">*</span>}</label>
                            <div
                              className={`practice-select-trigger${isViewMode ? ' practice-select-trigger--readonly' : ''}`}
                              onClick={() => !isViewMode && setShowPracticeDropdown(true)}
                            >
                              {selectedPractices.length > 0 && (
                                selectedPractices.map((practice) => (
                                  <span key={practice.practiceId} className="practice-tag">
                                    {practice.practiceName}
                                    {!isViewMode && (
                                      <button
                                        type="button"
                                        className="practice-tag-remove"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRemovePractice(practice.practiceId);
                                        }}
                                      >
                                        ×
                                      </button>
                                    )}
                                  </span>
                                ))
                              )}
                              {!isViewMode && (
                                <input
                                  type="text"
                                  className="practice-select-input"
                                  placeholder="Click to select practices..."
                                  value={practiceSearchTerm}
                                  onFocus={() => setShowPracticeDropdown(true)}
                                  onChange={(e) => {
                                    setPracticeSearchTerm(e.target.value);
                                    setShowPracticeDropdown(true);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              )}
                              {isViewMode && selectedPractices.length === 0 && (
                                <span className="practice-select-readonly-empty">None</span>
                              )}
                            </div>

                            {!isViewMode && showPracticeDropdown && (
                              <div className="practice-dropdown">
                                {loadingPractices ? (
                                  <div className="practice-dropdown-empty">Loading practices...</div>
                                ) : practices.length === 0 ? (
                                  <div className="practice-dropdown-empty">No practices found</div>
                                ) : practices
                                  .filter((p) => !selectedPractices.find((sp) => sp.practiceId === p.id))
                                  .filter((practice) =>
                                    matchesWordStart(
                                      practice.practice_name || `Practice ${practice.id}`,
                                      practiceSearchTerm
                                    )
                                  ).length === 0 ? (
                                  <div className="practice-dropdown-empty">No matching practices</div>
                                ) : (
                                  practices
                                    .filter((p) => !selectedPractices.find((sp) => sp.practiceId === p.id))
                                    .filter((practice) =>
                                      matchesWordStart(
                                        practice.practice_name || `Practice ${practice.id}`,
                                        practiceSearchTerm
                                      )
                                    )
                                    .map((practice) => (
                                      <div
                                        key={practice.id}
                                        className="practice-dropdown-item"
                                        onClick={() => handlePracticeClick(practice)}
                                      >
                                        {practice.practice_name || `Practice ${practice.id}`}
                                      </div>
                                    ))
                                )}
                              </div>
                            )}
                            {errors.practice_ids && (
                              <span className="text-red-500" style={{ fontSize: '11px', marginTop: '4px', display: 'block' }}>
                                {errors.practice_ids}
                              </span>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                  </div>
                </div>
              </fieldset>
            </form>
          </div>
          <div className="pif-footer">
            {editId ? (
              <button
                type="button"
                className="pif-events-log-btn"
                onClick={() => setShowEventLogsModal(true)}
                title="View Admin Event Logs"
              >
                <FontAwesomeIcon icon={faList} className="me-2" />
                Event Logs
              </button>
            ) : (
              <span />
            )}
            {!isViewMode && (
              <button type="button" className="pif-save-btn" disabled={isSubmitting || (isEditMode && !isChanged)} onClick={handleSubmit}>
                <Save size={18} />
                {isSubmitting
                  ? 'Saving...'
                  : isSystemAdmin
                    ? (isEditMode ? 'Update System Admin' : 'Save System Admin')
                    : (isEditMode ? 'Update Practice Admin' : 'Save Practice Admin')}
              </button>
            )}
          </div>
        </div>
      </div>

      {showEventLogsModal && editId && (
        <div ref={logOverlayRef} className="practices-list-modal-overlay" style={{ zIndex: 110000 }}>
          <div
            ref={logContainerRef}
            className="practices-list-modal-container"
            style={{ maxWidth: '900px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="practices-list-modal-header">
              <h5 className="practices-list-modal-title">
                {formData.firstName || formData.lastName
                  ? `${formData.firstName} ${formData.lastName} — Event Logs`
                  : 'Admin Event Logs'}
              </h5>
              <button
                type="button"
                className="practices-list-modal-close"
                onClick={() => setShowEventLogsModal(false)}
              >
                ×
              </button>
            </div>
            <div className="practices-list-modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <EventLogSection moduleId="Admin" targetUserId={editId} practiceId={practiceId} />
            </div>
          </div>
        </div>
      )}

      {/* Validation Warning Modal */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} centered className="add-admin-validation-modal">
        <Modal.Body className="add-admin-modal-body" style={{ padding: '30px', textAlign: 'center' }}>
          <div className="add-admin-modal-icon-wrapper" style={{ marginBottom: '20px' }}>
            <Info size={40} color="#1A252F" />
          </div>
          <h4 className="add-admin-modal-title" style={{ color: '#1A252F', marginBottom: '20px', fontWeight: '600' }}>Fill all required fields</h4>
          <button type="button" className="add-admin-modal-btn" onClick={() => setShowValidationModal(false)} style={{ backgroundColor: '#1A252F', color: '#fff', border: 'none', padding: '10px 30px', borderRadius: '4px', cursor: 'pointer', width: '100%' }}>
            OK
          </button>
        </Modal.Body>
      </Modal>
    </>
  );
};


export default AddAdminModal;


export { AddAdminModal };
